import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { theme } from '../../styles/theme';

const ProductCard = ({ product, variant = 'default' }) => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isAuthenticated, isApproved, pricingTier, loading: authLoading } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);

  const minSwipeDistance = 50;

  // Helper function to check if a sale is currently active
  const isSaleActive = (saleDiscountPercent, saleStartDate, saleEndDate) => {
    // Must have discount percent and end date (matching backend logic)
    if (!saleDiscountPercent || !saleEndDate) return false;
    
    const now = new Date();
    const end = new Date(saleEndDate);
    
    // Check if sale has ended
    if (end < now) return false;
    
    // Check if sale hasn't started yet (start date is optional)
    if (saleStartDate) {
      const start = new Date(saleStartDate);
      if (start > now) return false;
    }
    
    return true;
  };

  // Helper to check if sale applies to current user's tier
  const isSaleApplicableToTier = (saleApplicableTiers) => {
    if (!saleApplicableTiers || saleApplicableTiers.length === 0) {
      return true; // If no tiers specified, sale applies to all
    }
    return saleApplicableTiers.includes(pricingTier);
  };

  // Check if product has an active sale that applies to the current user
  const hasActiveSale = React.useMemo(() => {
    // Only show sale if user is authenticated
    if (!isAuthenticated) {
      return false;
    }

    // Check product-level sale
    if (isSaleActive(product.sale_discount_percent, product.sale_start_date, product.sale_end_date) && 
        isSaleApplicableToTier(product.sale_applicable_tiers)) {
      return true;
    }
    // Check subcategories for active sales
    if (product.subcategories && product.subcategories.length > 0) {
      return product.subcategories.some(sub => 
        isSaleActive(sub.sale_discount_percent, sub.sale_start_date, sub.sale_end_date) &&
        isSaleApplicableToTier(sub.sale_applicable_tiers)
      );
    }
    return false;
  }, [product, isAuthenticated, pricingTier]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      if (isLeftSwipe) {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      } else {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  const handleCardClick = (e) => {
    // If we just swiped (checked by looking if touchEnd exists and distance is large), don't navigate
    // However, touchEnd is reset on touchStart. 
    // The simplified browser behavior usually prevents click on swipe.
    // We'll rely on default behavior for now.
    navigate(`/product/${product.id}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    
    // Prevent adding products with invalid price (0.00)
    const price = Number(product.price) || 0;
    if (price <= 0) {
      alert('Unable to add product to cart. Please refresh the page and try again.');
      return;
    }
    
    addItem(product, 1).catch((err) => {
      alert(err.message || 'This item couldn’t be added to your cart.');
    });
  };

  const handleViewProduct = (e) => {
    e.stopPropagation();
    navigate(`/product/${product.id}`);
  };

  // Check if product has variants or components
  const hasVariants = product.subcategories && product.subcategories.length > 0;
  const hasComponents = product.components && product.components.length > 0;
  const requiresDetailView = hasVariants || hasComponents;

  // Get images array (limit to 6 images)
  const images = product.images && product.images.length > 0 
    ? product.images.slice(0, 6) 
    : [];

  const hasMultipleImages = images.length > 1;

  // Clean variant for modern e-commerce look
  if (variant === 'clean') {
    return (
      <div 
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setCurrentImageIndex(0); // Reset to first image when leaving card
        }}
        style={{
          backgroundColor: '#FFF',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
        }}
      >
        {/* Image Container */}
        <div 
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            position: 'relative',
            width: '100%',
            paddingTop: '100%', // 1:1 aspect ratio (square)
            backgroundColor: '#F8F8F8',
            marginBottom: '16px',
            overflow: 'hidden',
            touchAction: 'pan-y', // Allow vertical scroll but capture horizontal swipes
          }}>
          {images.length > 0 ? (
            <img
              src={images[currentImageIndex]}
              alt={product.name}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              }}
            />
          ) : (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: '#D1D1D1',
            }}>
              📦
            </div>
          )}

          {/* Sale Badge - Top priority */}
          {hasActiveSale && (
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: '#DC2626',
              color: '#FFF',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}>
              Sale
            </div>
          )}

          {/* Stock Badge - Only show if out of stock or low stock */}
          {product.stock_qty === 0 ? (
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              backgroundColor: '#000',
              color: '#FFF',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Out of Stock
            </div>
          ) : product.stock_qty > 0 && product.stock_qty < 10 ? (
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              backgroundColor: '#CC5500',
              color: '#FFF',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Low Stock
            </div>
          ) : null}

          {/* Multiple Images Indicator - Interactive */}
          {hasMultipleImages && (
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '6px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '6px 8px',
              borderRadius: '12px',
              backdropFilter: 'blur(4px)',
            }}>
              {images.map((_, idx) => (
                <div 
                  key={idx}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  style={{
                    width: currentImageIndex === idx ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: currentImageIndex === idx ? '#FFF' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div style={{ padding: '0 4px' }}>
          {/* Product Name */}
          <h3 style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '15px',
            fontWeight: 400,
            color: '#000',
            marginBottom: '6px',
            lineHeight: 1.4,
            minHeight: '42px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {product.name}
          </h3>

          {/* SKU */}
          <p style={{
            fontSize: '12px',
            color: '#999',
            marginBottom: '8px',
          }}>
            SKU: {product.sku}
          </p>

          {/* Price */}
          <div style={{
            marginBottom: '12px',
          }}>
            {product.price_visible || (isAuthenticated && isApproved) ? (
              (product.price === null || product.price === undefined || Number(product.price) <= 0) ? (
                <p style={{
                  fontSize: '13px',
                  color: '#CC5500',
                  fontWeight: 500,
                  lineHeight: '1.4',
                }}>
                  Click on product to see price
                </p>
              ) : (
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#000',
                }}>
                  ${(Number(product.price) || 0).toFixed(2)}
                  {product.subcategories && product.subcategories.length > 0 && (
                    <span style={{
                      fontSize: '12px',
                      color: '#666',
                      fontWeight: 400,
                      marginLeft: '4px',
                    }}>
                      +
                    </span>
                  )}
                </p>
              )
            ) : authLoading ? (
              <p style={{
                fontSize: '14px',
                color: '#999',
                fontStyle: 'italic',
              }}>
                Loading...
              </p>
            ) : isAuthenticated && !isApproved ? (
              <p style={{
                fontSize: '13px',
                color: '#CC5500',
                fontWeight: 500,
                lineHeight: '1.4',
              }}>
                Prices available when account is approved
              </p>
            ) : (
              <p style={{
                fontSize: '14px',
                color: '#666',
                fontStyle: 'italic',
              }}>
                Login to see price
              </p>
            )}
          </div>

          {/* Variants Badge */}
          {product.subcategories && product.subcategories.length > 0 && (
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '8px',
            }}>
              {product.subcategories.length} option{product.subcategories.length !== 1 ? 's' : ''} available
            </div>
          )}

          {/* Action Button (View Product for variants/components, Add to Cart otherwise) */}
          {(product.price_visible || isApproved) && product.stock_qty > 0 && product.price !== null && product.price !== undefined && Number(product.price) > 0 && (
            <button
              onClick={requiresDetailView ? handleViewProduct : handleAddToCart}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#000',
                color: '#FFF',
                border: 'none',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                opacity: isHovered ? 1 : 0,
                transform: isHovered ? 'translateY(0)' : 'translateY(10px)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000';
              }}
            >
              {requiresDetailView ? 'View Product' : 'Add to Cart'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default variant (existing design)
  return (
    <div 
      onClick={handleCardClick}
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: theme.shadows.sm,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = theme.shadows.lg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme.shadows.sm;
      }}
    >
      {/* Image */}
      <div style={{
        width: '100%',
        height: '200px',
        backgroundColor: theme.colors.surfaceAlt,
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.md,
        overflow: 'hidden',
        position: 'relative',
      }}>
        {images.length > 0 ? (
          <img
            src={images[0]}
            alt={product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem',
            color: theme.colors.textLight,
          }}>
            📦
          </div>
        )}
      </div>

      {/* Product Name */}
      <h3 style={{
        fontFamily: theme.typography.fontFamily.primary,
        fontSize: theme.typography.fontSize.lg,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
      }}>
        {product.name}
      </h3>

      {/* SKU */}
      <p style={{
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.sm,
      }}>
        SKU: {product.sku}
      </p>

      {/* Price */}
      {product.price_visible || (isAuthenticated && isApproved) ? (
        (product.price === null || product.price === undefined || Number(product.price) <= 0) ? (
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.cta,
            fontWeight: theme.typography.fontWeight.medium,
            marginBottom: theme.spacing.sm,
            lineHeight: theme.typography.lineHeight.relaxed,
          }}>
            Click on product to see price
          </p>
        ) : (
          <p style={{
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.primary,
            marginBottom: theme.spacing.sm,
          }}>
            ${(Number(product.price) || 0).toFixed(2)}
            {product.subcategories && product.subcategories.length > 0 && (
              <span style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary,
                fontWeight: theme.typography.fontWeight.normal,
                marginLeft: theme.spacing.xs,
              }}>
                +
              </span>
            )}
          </p>
        )
      ) : authLoading ? (
        <p style={{
          fontSize: theme.typography.fontSize.base,
          color: theme.colors.textLight,
          fontStyle: 'italic',
          marginBottom: theme.spacing.sm,
        }}>
          Loading...
        </p>
      ) : isAuthenticated && !isApproved ? (
        <p style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.cta,
          fontWeight: theme.typography.fontWeight.medium,
          marginBottom: theme.spacing.sm,
          lineHeight: theme.typography.lineHeight.relaxed,
        }}>
          Prices available when account is approved
        </p>
      ) : (
        <p style={{
          fontSize: theme.typography.fontSize.base,
          color: theme.colors.textSecondary,
          fontStyle: 'italic',
          marginBottom: theme.spacing.sm,
        }}>
          Login to see price
        </p>
      )}

      {/* Stock Status - Only show if out of stock or low stock */}
      {product.stock_qty === 0 ? (
        <p style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.error,
          marginBottom: theme.spacing.md,
          fontWeight: theme.typography.fontWeight.medium,
        }}>
          Out of Stock
        </p>
      ) : product.stock_qty > 0 && product.stock_qty < 10 ? (
        <p style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.cta,
          marginBottom: theme.spacing.md,
          fontWeight: theme.typography.fontWeight.medium,
        }}>
          Low Stock
        </p>
      ) : null}
    </div>
  );
};

export default ProductCard;
